import { z } from "zod";

// ============================================================================
// RETELL API SUB-SCHEMAS
// ============================================================================

// Transcript segment (from RetellTranscriptSegment)
const RetellTranscriptSegmentSchema = z.object({
  role: z.enum(["agent", "user"]),
  content: z.string(),
  timestamp: z.number(),
  confidence: z.number().optional(),
});

// LLM Token Usage
const LlmTokenUsageSchema = z.object({
  prompt_tokens: z.number(),
  completion_tokens: z.number(),
  total_tokens: z.number(),
});

// Latency metrics
const LatencySchema = z.object({
  p50: z.number(),
  p90: z.number(),
  p95: z.number(),
  p99: z.number(),
  max: z.number(),
  min: z.number(),
  num: z.number(),
});

// Retell Call Analysis (from Retell API response)
const RetellCallAnalysisSchema = z.object({
  call_summary: z.string().optional(),
  call_successful: z.boolean().optional(),
  in_voicemail: z.boolean().optional(),
  user_sentiment: z.string().optional(),
  call_transcript: z.string().optional(),
});

// ============================================================================
// BASE RETELL CALL OUTPUT SCHEMA (matches backend DbRetellCallOutput)
// ============================================================================

// To sync with DbRetellCallOutput in backend/schemas/retell-call-output.ts
// This matches the retell_call_output_view which extracts fields from JSONB data
const BaseRetellCallOutputSchema = z.object({
  // Primary fields
  id: z.string(),

  // Relations
  callAttemptId: z.string(),
  retellCallId: z.string(),

  // Call info
  callType: z.string(), // 'web' | 'phone'
  callStatus: z.string(), // 'pending' | 'registered' | 'ongoing' | 'ended' | 'failed' | etc.

  // Timestamps
  createdAt: z.string(),
  updatedAt: z.string(),

  // Retell API fields (extracted from data JSONB in the view)
  retellAgentId: z.string().nullable().optional(),
  startTimestamp: z.number().nullable().optional(),
  endTimestamp: z.number().nullable().optional(),
  callDuration: z.number().nullable().optional(),

  // Contact info
  fromNumber: z.string().nullable().optional(),
  toNumber: z.string().nullable().optional(),

  // Results
  disconnectReason: z.string().nullable().optional(),
  recordingUrl: z.string().nullable().optional(),
  transcript: z.array(RetellTranscriptSegmentSchema).nullable().optional(),
  collectedDynamicVariables: z.record(z.string(), z.unknown()).nullable().optional(),

  // Analytics
  cost: z.number().nullable().optional(),
  llmTokenUsage: LlmTokenUsageSchema.nullable().optional(),
  latency: LatencySchema.nullable().optional(),
  callAnalysis: RetellCallAnalysisSchema.nullable().optional(),

  // Metadata
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const RetellCallOutputSchema = BaseRetellCallOutputSchema;

export const RetellCallOutputListItemSchema = BaseRetellCallOutputSchema;

export const RetellCallOutputListSchema = z.array(RetellCallOutputListItemSchema);

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type RetellTranscriptSegment = z.infer<typeof RetellTranscriptSegmentSchema>;
export type LlmTokenUsage = z.infer<typeof LlmTokenUsageSchema>;
export type Latency = z.infer<typeof LatencySchema>;
export type RetellCallAnalysis = z.infer<typeof RetellCallAnalysisSchema>;

export type RetellCallOutput = z.infer<typeof RetellCallOutputSchema>;
export type RetellCallOutputListItem = z.infer<typeof RetellCallOutputListItemSchema>;
